import { FC, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Heading,
  Input,
  Select as ChakraSelect,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { DirectoryForm } from '../models';
import { CenteredSpinner } from '../../../components';
import {
  useGetDirectoryByIdQuery,
  useCreateDirectoryMutation,
  useUpdateDirectoryMutation,
  useGetDirectoryTagsQuery,
  useGetAllRolesQuery,
  displayNotification,
} from '../../../services';
import {
  SavedDirectory as SavedDirectoryType,
  DirectoryType,
} from '../../../models';
import { useTypedSelector } from '../../../hooks';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const SavedDirectory: FC = () => {
  const { userDetails } = useTypedSelector(state => state.auth);

  const params = useParams();
  const { projectId, directoryId, type } = params;

  const { data: currentDirectory } = useGetDirectoryByIdQuery(
    Number(directoryId)
  );

  const { data: directoryTags, isLoading: isDirectoryTagsLoading } =
    useGetDirectoryTagsQuery();

  const { data: allRoles, isLoading: isAllRolesLoading } = useGetAllRolesQuery(
    {
      basicInfoId: Number(userDetails?.companyBasicInfo.id),
      page: 0,
      size: 10000,
    },
    {
      skip: !userDetails,
    }
  );

  const navigate = useNavigate();

  const [createDirectory] = useCreateDirectoryMutation();
  const [updateDirectory] = useUpdateDirectoryMutation();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<DirectoryForm>();

  const navigateToPreviousPage = (id = directoryId) => {
    let url = `/projects/${type}/${String(
      projectId
    )}/project-details/documents`;

    if (
      currentDirectory?.data.parentDirectory?.directoryTypeEnum !==
      DirectoryType.PROJECT
    )
      url += `/${String(id)}`;

    navigate(url);
  };

  async function onSubmit(data: DirectoryForm) {
    const { directoryName, directoryTags, deleteRoles, editRoles, viewRoles } =
      data;

    let directoryInfo: SavedDirectoryType = {
      directoryName,
      viewRoles: JSON.stringify([{ id: viewRoles }]),
      editRoles: JSON.stringify([{ id: editRoles }]),
      deleteRoles: JSON.stringify([{ id: deleteRoles }]),
    };

    if (directoryTags)
      directoryInfo.directoryTags = [{ id: Number(directoryTags) }];

    try {
      if (location.pathname.endsWith('update-directory')) {
        directoryInfo = {
          ...directoryInfo,
          id: Number(directoryId),
        };

        // `.unwrap()` only rejects on the transport shape: a body-level
        // failure resolved and the page navigated away with an undefined
        // parent id. Classify the raw reply instead.
        const response = await updateDirectory(directoryInfo);

        if (!isMutationSuccess(response)) {
          notifyMutationFailure(response, {
            title: 'Could not update this folder',
            description:
              'Your changes were not saved. Please review the form and try again.',
          });
          return;
        }

        const updatedDirectory = (
          response as {
            data: { data: { parentDirectory?: { id: number } } };
          }
        ).data;

        navigateToPreviousPage(
          String(updatedDirectory.data.parentDirectory?.id)
        );
      } else {
        directoryInfo = {
          ...directoryInfo,
          directoryPath: `${currentDirectory!.data.fullPath}/`,
          parentDirectory: {
            id: Number(directoryId),
          },
          project: {
            id: Number(projectId),
          },
          directoryTypeEnum: DirectoryType.PHASE,
        };
        const response = await createDirectory(directoryInfo);

        if (!isMutationSuccess(response)) {
          notifyMutationFailure(response, {
            title: 'Could not create this folder',
            description:
              'The folder was not created. Please review the form and try again.',
          });
          return;
        }

        navigateToPreviousPage();
      }
    } catch (error) {
      console.error(error);
      displayNotification({
        title: 'Something went wrong!',
        description:
          'Your changes were not saved. Please try this action later or contact BrickLynk support.',
        status: 'error',
      });
    }
  }

  useEffect(() => {
    if (
      currentDirectory?.data &&
      location.pathname.endsWith('update-directory')
    ) {
      const {
        directoryName,
        directoryTags,
        viewRoles,
        editRoles,
        deleteRoles,
      } = currentDirectory.data;

      reset({
        directoryName,
        directoryTags: directoryTags[0]
          ? String(directoryTags[0].id)
          : undefined,
        viewRoles: (JSON.parse(viewRoles) as { id: string }[])[0].id,
        editRoles: (JSON.parse(editRoles) as { id: string }[])[0].id,
        deleteRoles: (JSON.parse(deleteRoles) as { id: string }[])[0].id,
      });
    }
  }, [currentDirectory, reset]);

  if (isDirectoryTagsLoading || isAllRolesLoading) return <CenteredSpinner />;

  return (
    <Box>
      <HStack justify="space-between" mt="4" mb="12" align="center">
        <Heading color="custom_blue.1" fontSize="2xl">
          {location.pathname.endsWith('update-directory')
            ? 'Update ' + String(currentDirectory?.data.directoryName)
            : 'Create New Folder'}
        </Heading>

        <HStack mt={4} justify="end">
          <Button onClick={() => navigateToPreviousPage()}>Cancel</Button>
          <Button
            isLoading={isSubmitting}
            type="button"
            onClick={handleSubmit(onSubmit)}
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
          >
            Save
          </Button>
        </HStack>
      </HStack>

      <form className="flex flex-col gap-5">
        <Box>
          <VStack
            align="start"
            w="full"
            p="4"
            boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
            borderRadius="md"
            border="1px"
            borderColor="custom_gray.3"
            gap="4"
            mb="5"
          >
            <HStack gap="4" align="flex-start" w="full">
              <VStack color="custom_blue.1" align="start" flexBasis="25%">
                <Box fontSize="sm" fontWeight="semibold">
                  Folder Name
                </Box>
              </VStack>
              <FormControl
                isInvalid={!!errors.directoryName}
                flexBasis="50%"
                mt="2"
              >
                <Input
                  placeholder="Document Name"
                  {...register('directoryName', {
                    required: 'Folder name is required',
                  })}
                />
                <FormErrorMessage>
                  {errors.directoryName && errors.directoryName.message}
                </FormErrorMessage>
              </FormControl>
            </HStack>

            <HStack gap="4" align="flex-start" w="full">
              <VStack color="custom_blue.1" align="start" flexBasis="25%">
                <Box fontSize="sm" fontWeight="semibold">
                  Who can view?
                </Box>
              </VStack>
              <FormControl
                flexBasis="50%"
                mt="2"
                isInvalid={!!errors.viewRoles}
              >
                <ChakraSelect
                  {...register('viewRoles', {
                    required: 'View role is required',
                  })}
                >
                  <option />
                  {allRoles?.data.content.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </ChakraSelect>
                <FormErrorMessage>
                  {errors.viewRoles && errors.viewRoles.message}
                </FormErrorMessage>
              </FormControl>
            </HStack>

            <HStack gap="4" align="flex-start" w="full">
              <VStack color="custom_blue.1" align="start" flexBasis="25%">
                <Box fontSize="sm" fontWeight="semibold">
                  Who can edit?
                </Box>
                <Box fontSize="xs">
                  Editors can update, move, and archive the document.
                </Box>
              </VStack>
              <FormControl
                flexBasis="50%"
                mt="2"
                isInvalid={!!errors.editRoles}
              >
                <ChakraSelect
                  {...register('editRoles', {
                    required: 'Edit role is required',
                  })}
                >
                  <option />
                  {allRoles?.data.content.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </ChakraSelect>
                <FormErrorMessage>
                  {errors.viewRoles && errors.viewRoles.message}
                </FormErrorMessage>
              </FormControl>
            </HStack>

            <HStack gap="4" align="flex-start" w="full">
              <VStack color="custom_blue.1" align="start" flexBasis="25%">
                <Box fontSize="sm" fontWeight="semibold">
                  Who can delete?
                </Box>
              </VStack>
              <FormControl
                flexBasis="50%"
                mt="2"
                isInvalid={!!errors.deleteRoles}
              >
                <ChakraSelect
                  {...register('deleteRoles', {
                    required: 'Delete role is required',
                  })}
                >
                  <option />
                  {allRoles?.data.content.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </ChakraSelect>
                <FormErrorMessage>
                  {errors.deleteRoles && errors.deleteRoles.message}
                </FormErrorMessage>
              </FormControl>
            </HStack>

            <HStack gap="4" align="flex-start" w="full">
              <VStack color="custom_blue.1" align="start" flexBasis="25%">
                <Box fontSize="sm" fontWeight="semibold">
                  Tags
                </Box>
              </VStack>
              <FormControl flexBasis="50%" mt="2">
                <ChakraSelect {...register('directoryTags')}>
                  <option />
                  {directoryTags?.data.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </ChakraSelect>
              </FormControl>
            </HStack>
          </VStack>
        </Box>
      </form>
    </Box>
  );
};
